import { Box, Checkbox, TextField } from '@color/continuum';
import { useCallback, useMemo, useState } from 'react';

import type { SelectedConfirmationsTableExtensionColumn } from './SelectedConfirmationsTable';
import type { VariantConfirmation } from './types';

interface PrimerFailureState {
  failPrimers: { notes: string } | null;
}

export interface PrimerFailurePayload {
  primerNotes: string;
}

const NOT_FAILING: PrimerFailureState = { failPrimers: null };

export const PRIMER_NOTES_BLANK_HELPER_TEXT = 'Required to fail primer';

/**
 * Shared "Fail primer / Primer notes" extension columns plus submit-validation
 * for any dialog that lists confirmations and lets the user fail their active
 * primer set alongside the dialog's main action (cancel, fail/requeue, ...).
 *
 * Per-row state lives inside the hook; consumers only render the columns,
 * read ``isPrimerNotesIncomplete`` to gate submission, and call
 * ``getFailPrimers(id)`` to build the API payload for each confirmation.
 *
 * State is initialized from `confirmations` at mount and is not re-synced when
 * the prop changes; mount with the confirmation list you intend to act on.
 */
export function usePrimerFailureColumns(confirmations: VariantConfirmation[]): {
  extensionColumns: SelectedConfirmationsTableExtensionColumn[];
  isPrimerNotesIncomplete: boolean;
  getFailPrimers: (confirmationId: number) => PrimerFailurePayload | null;
} {
  const [primerStates, setPrimerStates] = useState<Record<number, PrimerFailureState>>(() =>
    Object.fromEntries(
      confirmations.filter((c) => c.primer_set != null).map((c) => [c.id, NOT_FAILING])
    )
  );

  const handleFailPrimersToggle = useCallback(
    (confirmationId: number, shouldFail: boolean, seedNotes: string) => {
      setPrimerStates((prev) => ({
        ...prev,
        [confirmationId]: shouldFail ? { failPrimers: { notes: seedNotes } } : NOT_FAILING,
      }));
    },
    []
  );

  const handlePrimerNotesChange = useCallback((confirmationId: number, notes: string) => {
    setPrimerStates((prev) => {
      const current = prev[confirmationId];
      if (current?.failPrimers == null) return prev;
      return {
        ...prev,
        [confirmationId]: { failPrimers: { notes } },
      };
    });
  }, []);

  const extensionColumns = useMemo<SelectedConfirmationsTableExtensionColumn[]>(() => {
    if (!confirmations.some((c) => c.primer_set != null)) return [];

    const failPrimersColumn: SelectedConfirmationsTableExtensionColumn = {
      key: 'fail-primers',
      header: 'Fail primer',
      width: '70px',
      renderCell: (confirmation) => {
        if (confirmation.primer_set == null) return null;
        const state = primerStates[confirmation.id] ?? NOT_FAILING;
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Checkbox
              label=""
              value={state.failPrimers != null}
              onChange={(e) =>
                handleFailPrimersToggle(
                  confirmation.id,
                  e.target.checked,
                  confirmation.primer_set?.notes ?? ''
                )
              }
            />
          </Box>
        );
      },
    };

    const primerNotesColumn: SelectedConfirmationsTableExtensionColumn = {
      key: 'primer-notes',
      header: 'Primer notes',
      renderCell: (confirmation) => {
        if (confirmation.primer_set == null) return null;
        const state = primerStates[confirmation.id] ?? NOT_FAILING;
        if (state.failPrimers == null) return (confirmation.primer_set.notes ?? '').trim();

        const isBlank = state.failPrimers.notes.trim() === '';
        return (
          <Box sx={{ minWidth: 200 }}>
            <TextField
              label="Primer notes"
              size="small"
              multiline
              minRows={1}
              maxRows={6}
              value={state.failPrimers.notes}
              optionalIndicatorIsEnabled={false}
              onChange={(e) => handlePrimerNotesChange(confirmation.id, e.target.value)}
              fullWidth
              required
              error={isBlank}
              helperText={isBlank ? PRIMER_NOTES_BLANK_HELPER_TEXT : undefined}
            />
          </Box>
        );
      },
    };

    return [failPrimersColumn, primerNotesColumn];
  }, [confirmations, primerStates, handleFailPrimersToggle, handlePrimerNotesChange]);

  const isPrimerNotesIncomplete = useMemo(
    () =>
      Object.values(primerStates).some(
        (state) => state.failPrimers != null && state.failPrimers.notes.trim() === ''
      ),
    [primerStates]
  );

  const getFailPrimers = useCallback(
    (confirmationId: number): PrimerFailurePayload | null => {
      const failPrimers = primerStates[confirmationId]?.failPrimers;
      return failPrimers != null ? { primerNotes: failPrimers.notes } : null;
    },
    [primerStates]
  );

  return { extensionColumns, isPrimerNotesIncomplete, getFailPrimers };
}
